import React, { useContext, useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import Axios from "axios";
import FormData from "form-data";
import { DatabaseContext } from "./DatabaseContext";
import { UserContext } from "./UserContext";
import { PaneContext } from "./PaneContext";

export const PatronContext = React.createContext();

export const PatronProvider = ({ children }) => {
  const { apiUrl } = useContext(DatabaseContext);
  const { Site, email } = useContext(UserContext);
  const { updatePreviewPane } = useContext(PaneContext);
  const { siteCode } = Site;
  const [patron, setPatron] = useState({});
  const [patrons, setPatrons] = useState({});
  const [wondeImportDate, setWondeImportDate] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const handlePatronChange = (e) => {
    const { name, value } = e.target;
    setPatron((s) => ({ ...s, [name]: value }));
  };

  const selectPatron = async (patron) => {
    //Clear patron if no value provided

    if (patron) {
      if (patron.hasPhoto) {
        updatePreviewPane(
          `${apiUrl}/uploads/${Site.siteCode}/${patron.externalID}.jpg`
        );
      } else {
        updatePreviewPane(null);
      }
      setPatron(patron);

      if (patron.dateOfBirth === null) {
        patron.dateOfBirth = "dd-mm-yyyy";
      }
      if (patron.street === null) {
        patron.street = " ";
      }
      if (patron.suburb === null) {
        patron.suburb = " ";
      }
      if (patron.state === null) {
        patron.state = " ";
      }
      if (patron.postcode === null) {
        patron.postcode = " ";
      }
      if (patron.curriculum === null) {
        patron.curriculum = " ";
      }
      if (patron.custom1 === null) {
        patron.custom1 = " ";
      }
      if (patron.custom2 === null) {
        patron.custom2 = " ";
      }
      if (patron.custom3 === null) {
        patron.custom3 = " ";
      }
      if (patron.hasPhoto === null) {
        patron.hasPhoto = " ";
      }
      if (patron.PSN === null) {
        patron.PSN = " ";
      }
      if (patron.PatronID === null) {
        patron.PatronID = " ";
      }

      patron.DisExt = true;
    } else {
      setPatron({});
    }
  };

  const incrementPatronYearGroup = async () => {
    try {
      const res = await Axios.post(
        `${apiUrl}/api/maintenance/incrementYearGroup/${siteCode}`
      );
      refreshPatrons();
    } catch (err) {
      enqueueSnackbar(err.response.data.error, {
        variant: "error",
        autoHideDuration: 6000,
      });
    }
  };

  const incrementPatronYearGroupAndRemove = async (Dlt) => {
    try {
      const res = await Axios.post(
        `${apiUrl}/api/maintenance/incrementYearGroup/${siteCode}`,
        { dlt: Dlt }
      );
      refreshPatrons();
    } catch (err) {
      enqueueSnackbar(err.response.data.error, {
        variant: "error",
        autoHideDuration: 6000,
      });
    }
  };

  const refreshPatrons = async () => {
    try {
      const res = await Axios.get(`${apiUrl}/api/patrons/${siteCode}`);
      if (res.data.success) {
        setPatrons(res.data.data);
      }
    } catch (err) {
      enqueueSnackbar(err.response.data.error, {
        variant: "error",
        autoHideDuration: 6000,
      });
    }
  };

  const getAllPatrons = async () => {
    try {
      const res = await Axios.get(`${apiUrl}/api/patrons/${siteCode}`);

      if (res.data.success) {
        setPatrons(res.data.data);
        return res.data.data;
      }
    } catch (err) {
      enqueueSnackbar(err.response.data.error, {
        variant: "error",
        autoHideDuration: 6000,
      });
    }
  };

  const createPatron = async (data) => {
    try {
      const res = await Axios.post(`${apiUrl}/api/patrons/${siteCode}`, data);

      if (res.data.data.success == "true") {
        refreshPatrons();
        enqueueSnackbar(`Created ${data.firstName} ${data.lastName}`, {
          variant: "success",
          autoHideDuration: 6000,
          style: { fontSize: "18px" },
        });
        return res.data.data;
      }
      if (res.data.data.success == "false") {
        enqueueSnackbar(res.data.data.data, {
          variant: "error",
          autoHideDuration: 6000,
          style: { fontSize: "18px" },
        });
      }
    } catch (err) {
      enqueueSnackbar(err.response.data.error, {
        variant: "error",
        autoHideDuration: 6000,
        style: { fontSize: "18px" },
      });
    }
  };

  const updatePatron = async (externalID, data) => {
    try {
      const res = await Axios.put(
        `${apiUrl}/api/patrons/${siteCode}/${encodeURIComponent(externalID)}`,
        data
      );
      if (res.data.data.success == "true") {
        refreshPatrons();
        enqueueSnackbar(`Updated ${data.firstName} ${data.lastName}`, {
          variant: "success",
          autoHideDuration: 6000,
          style: { fontSize: "15px" },
        });
        return res.data.data;
      }
      if (res.data.data.success == "false") {
        enqueueSnackbar(res.data.data.data, {
          variant: "error",
          autoHideDuration: 6000,
          style: { fontSize: "15px" },
        });
      }
    } catch (err) {
      console.log(err);
      enqueueSnackbar(err.response.data.error, {
        variant: "error",
        autoHideDuration: 6000,
        style: { fontSize: "15px" },
      });
    }
  };

  const uploadPhoto = async (externalID, photo) => {
    let form = new FormData();
    form.append("photo", photo);
    try {
      const res = await Axios.post(
        `${apiUrl}/api/patrons/${siteCode}/${encodeURIComponent(
          externalID
        )}/photo`,
        form
      );
      if (res.data.success) {
        enqueueSnackbar("Uploaded photo", {
          variant: "success",
          style: { fontSize: "15px" },
          autoHideDuration: 6000,
        });
        refreshPatrons();
        return res.data.data;
      }
    } catch (err) {
      enqueueSnackbar(err.response.data.error, {
        variant: "error",
        autoHideDuration: 6000,
        style: { fontSize: "15px" },
      });
    }
  };

  const removePhoto = async (externalID) => {
    try {
      const res = await Axios.delete(
        `${apiUrl}/api/patrons/${siteCode}/${encodeURIComponent(
          externalID
        )}/photo`
      );
      if (res.data.success) {
        enqueueSnackbar("Removed photo", {
          variant: "success",
          autoHideDuration: 6000,
          style: { fontSize: "15px" },
        });
        refreshPatrons();
      }
    } catch (err) {
      enqueueSnackbar(err.response.data.error, {
        variant: "error",
        autoHideDuration: 6000,
        style: { fontSize: "15px" },
      });
    }
  };

  const deletePatron = async (externalID) => {
    try {
      const res = await Axios.delete(
        `${apiUrl}/api/patrons/${siteCode}/${encodeURIComponent(externalID)}`
      );
      if (res.data.success) {
        refreshPatrons();
        enqueueSnackbar(`Deleted patron`, { variant: "success" });
      }
    } catch (err) {
      enqueueSnackbar(err.response.data.error, {
        variant: "error",
        autoHideDuration: 6000,
      });
    }
  };

  const deleteAllPatrons = async () => {
    try {
      const res = await Axios.delete(`${apiUrl}/api/patrons/${siteCode}`);
      if (res.data.success) {
        refreshPatrons();
        enqueueSnackbar("All students deleted Successfully", {
          variant: "success",
        });
      }
    } catch (err) {
      enqueueSnackbar(err.response.data.error, {
        variant: "error",
        autoHideDuration: 6000,
      });
    }
  };

  //${email}/
  const importPatrons = async (patrons) => {
    try {
      const res = await Axios.post(
        `${apiUrl}/api/patrons/${siteCode}/import`,
        patrons
      );

      if (res.data.success == true) {
        enqueueSnackbar(
          `Imported ${res.data.data[0].inserted} patron${
            res.data.data[0].inserted !== 1 ? "s" : ""
          }`,
          { variant: "success" }
        );

        enqueueSnackbar(
          `Updated ${res.data.data[0].updated} patron${
            res.data.data[0].updated !== 1 ? "s" : ""
          }`,
          { variant: "success" }
        );

        refreshPatrons();
      }
    } catch (err) {
      enqueueSnackbar(`Couldn't import data:${err.message}`, {
        variant: "error",
        autoHideDuration: 6000,
      });
    }
  };

  const FirstTimeOrderYear = async (yearGroup) => {
    try {
      const res = await Axios.get(
        `${apiUrl}/api/library/firsttimeorderyear/?siteCode=${siteCode}&yearGroup=${yearGroup}`
      );
      if (res.data.success) {
        //Load these patrons in order queue

        setPatrons(res.data.data[0]);
        return res.data.data[0];
      }
    } catch (err) {
      enqueueSnackbar(err.response.data.error, { variant: "error" });
    }
  };

  const GetWondeImportDate = async () => {
    try {
      const res = await Axios.get(
        `${apiUrl}/api/library/wondeimportdate/${siteCode}`
      );

      setWondeImportDate(res.data.data);
    } catch (err) {
      enqueueSnackbar(err.response.data.error, {
        variant: "error",
        autoHideDuration: 6000,
      });
    }
  };

  useEffect(() => {
    Axios.get(`${apiUrl}/api/patrons/${siteCode}`)
      .then((res) => {
        if (res.data.success) {
          setPatrons(res.data.data);
        }
      })
      .catch((err) =>
        enqueueSnackbar(err.response.data.error, {
          variant: "error",
          autoHideDuration: 6000,
        })
      );
    //eslint-disable-next-line
  }, [siteCode, apiUrl]);

  return (
    <PatronContext.Provider
      value={{
        patron,
        patrons,
        setPatrons,
        handlePatronChange,
        selectPatron,
        getAllPatrons,
        refreshPatrons,
        createPatron,
        updatePatron,
        uploadPhoto,
        removePhoto,
        deletePatron,
        deleteAllPatrons,
        importPatrons,
        incrementPatronYearGroup,
        FirstTimeOrderYear,
        GetWondeImportDate,
        incrementPatronYearGroupAndRemove,
        wondeImportDate,
      }}
    >
      {children}
    </PatronContext.Provider>
  );
};
